import { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, Save, X, Loader2, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { adminApi } from '@/lib/api';
import { toast } from 'sonner';

const emptyItem = {
  title: '',
  description: '',
  image_url: '',
  tags: [],
  link: '',
  order: 0,
  active: true,
};

const PortfolioManager = () => {
  const [portfolio, setPortfolio] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [tagsText, setTagsText] = useState('');

  useEffect(() => {
    fetchPortfolio();
  }, []);

  const fetchPortfolio = async () => {
    try {
      const response = await adminApi.getPortfolio();
      setPortfolio(response.data);
    } catch (error) {
      console.error('Error fetching portfolio:', error);
      toast.error('Failed to load portfolio');
    } finally {
      setLoading(false);
    }
  };

  const openDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      setTagsText(item.tags?.join(', ') || '');
    } else {
      setEditingItem({ ...emptyItem, order: portfolio.length });
      setTagsText('');
    }
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingItem(null);
    setTagsText('');
  };

  const handleChange = (e) => {
    setEditingItem({ ...editingItem, [e.target.name]: e.target.value });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const itemData = {
        ...editingItem,
        tags: tagsText.split(',').map(t => t.trim()).filter(t => t),
      };

      if (editingItem.id) {
        await adminApi.updatePortfolioItem(editingItem.id, itemData);
        toast.success('Portfolio item updated');
      } else {
        await adminApi.createPortfolioItem(itemData);
        toast.success('Portfolio item created');
      }
      fetchPortfolio();
      closeDialog();
    } catch (error) {
      console.error('Error saving portfolio:', error);
      toast.error('Failed to save portfolio item');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    try {
      await adminApi.deletePortfolioItem(id);
      toast.success('Portfolio item deleted');
      fetchPortfolio();
    } catch (error) {
      console.error('Error deleting portfolio:', error);
      toast.error('Failed to delete item');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div data-testid="portfolio-manager">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-heading font-bold mb-2">Portfolio</h1>
          <p className="text-muted-foreground">Showcase your best work.</p>
        </div>
        <Button onClick={() => openDialog()} className="btn-primary" data-testid="add-portfolio-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Project
        </Button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {portfolio.map((item) => (
          <div key={item.id} className="admin-card group" data-testid={`portfolio-card-${item.id}`}>
            <div className="aspect-video rounded-lg overflow-hidden mb-4 bg-white/5">
              {item.image_url ? (
                <img src={item.image_url} alt={item.title} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                  No Image
                </div>
              )}
            </div>
            <h3 className="font-heading font-semibold mb-2">{item.title}</h3>
            <p className="text-muted-foreground text-sm mb-3 line-clamp-2">{item.description}</p>
            <div className="flex flex-wrap gap-1 mb-4">
              {item.tags?.slice(0, 3).map((tag, i) => (
                <span key={i} className="px-2 py-1 bg-primary/10 text-primary text-xs rounded">
                  {tag}
                </span>
              ))}
            </div>
            <div className="flex items-center justify-between">
              <span className={item.active ? 'badge-success' : 'badge-error'}>
                {item.active ? 'Active' : 'Inactive'}
              </span>
              <div className="flex gap-2">
                {item.link && (
                  <a href={item.link} target="_blank" rel="noopener noreferrer">
                    <Button variant="ghost" size="icon">
                      <ExternalLink className="w-4 h-4" />
                    </Button>
                  </a>
                )}
                <Button variant="ghost" size="icon" onClick={() => openDialog(item)} data-testid={`edit-portfolio-${item.id}`}>
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDelete(item.id)}
                  className="text-red-400 hover:text-red-300"
                  data-testid={`delete-portfolio-${item.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        ))}
        {portfolio.length === 0 && (
          <div className="col-span-full text-center text-muted-foreground py-12">
            No portfolio items yet. Add your first project.
          </div>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#0f172a] border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingItem?.id ? 'Edit Project' : 'Add Project'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="form-group">
              <label className="form-label">Project Title</label>
              <Input
                name="title"
                value={editingItem?.title || ''}
                onChange={handleChange}
                placeholder="E-commerce Platform"
                className="input-neon"
                data-testid="portfolio-title-input"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Description</label>
              <Textarea
                name="description"
                value={editingItem?.description || ''}
                onChange={handleChange}
                placeholder="Full e-commerce solution with payment integration"
                rows={3}
                className="input-neon resize-none"
                data-testid="portfolio-description-input"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Image URL</label>
              <Input
                name="image_url"
                value={editingItem?.image_url || ''}
                onChange={handleChange}
                placeholder="https://images.unsplash.com/..."
                className="input-neon"
                data-testid="portfolio-image-input"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Tags (comma-separated)</label>
              <Input
                value={tagsText}
                onChange={(e) => setTagsText(e.target.value)}
                placeholder="React, Node.js, Stripe"
                className="input-neon"
                data-testid="portfolio-tags-input"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Project Link (optional)</label>
              <Input
                name="link"
                value={editingItem?.link || ''}
                onChange={handleChange}
                placeholder="https://example.com"
                className="input-neon"
                data-testid="portfolio-link-input"
              />
            </div>
            <div className="flex items-center justify-between">
              <label className="form-label">Active</label>
              <Switch
                checked={editingItem?.active ?? true}
                onCheckedChange={(checked) => setEditingItem({ ...editingItem, active: checked })}
                data-testid="portfolio-active-switch"
              />
            </div>
            <div className="flex gap-2 justify-end pt-4">
              <Button variant="ghost" onClick={closeDialog}>
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving} className="btn-primary" data-testid="save-portfolio-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PortfolioManager;
